export interface AgentAvatarBlob {
    color: string;
    cx: number;
    cy: number;
    r: number;
}

export interface AgentAvatarSpec {
    blobs: [AgentAvatarBlob, AgentAvatarBlob, AgentAvatarBlob];
}

const BASE_ANGLES = [30, 150, 270];
const BLOB_DIST = 34;
const BLOB_RADIUS = 38;
export const AGENT_AVATAR_BLUR_STD_DEVIATION = 24;

// Analogous-hue spread between neighboring blobs, in degrees — keeps the
// triad looking like one cohesive gradient rather than clashing complements.
const HUE_SPREAD_MIN = 25;
const HUE_SPREAD_MAX = 55;
const SATURATION_RANGE: [number, number] = [58, 78]; // %
const LIGHTNESS_RANGE: [number, number] = [46, 62]; // %

function hashString(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    }
    return hash;
}

// Three analogous hues around a hash-derived base hue, rather than picking
// from a small fixed palette. A handful of fixed triads meant avatars
// collided into visually identical results as soon as a list had more than
// a few agents (the birthday paradox bites hard on a pool of 8). Deriving
// hue, spread, saturation, and lightness from different bit-slices of the
// same hash gives every id its own point in a near-continuous color space
// instead, while staying just as deterministic and just as cheap.
function triadForId(hash: number): [string, string, string] {
    const baseHue = hash % 360;
    const direction = (hash >>> 24) % 2 === 0 ? 1 : -1;
    const spreadRange = HUE_SPREAD_MAX - HUE_SPREAD_MIN;
    const spread1 = HUE_SPREAD_MIN + ((hash >>> 4) % spreadRange);
    const spread2 = HUE_SPREAD_MIN + ((hash >>> 12) % spreadRange);
    const [satMin, satMax] = SATURATION_RANGE;
    const [lightMin, lightMax] = LIGHTNESS_RANGE;
    const saturation = satMin + ((hash >>> 18) % (satMax - satMin));
    const lightness = lightMin + ((hash >>> 26) % (lightMax - lightMin));

    const hue2 = (baseHue + direction * spread1 + 360) % 360;
    const hue3 = (hue2 + direction * spread2 + 360) % 360;

    const toHsl = (h: number) => `hsl(${h} ${saturation}% ${lightness}%)`;
    return [toHsl(baseHue), toHsl(hue2), toHsl(hue3)];
}

export function agentAvatarSpec(id: string): AgentAvatarSpec {
    const hash = hashString(id);
    const triad = triadForId(hash);
    const rotation = (hash >>> 8) % 3;
    const jitterDeg = ((hash >>> 16) % 41) - 20;

    const blobs = BASE_ANGLES.map((baseAngle, i) => {
        const angle = ((baseAngle + jitterDeg) * Math.PI) / 180;
        return {
            color: triad[(i + rotation) % 3],
            cx: 50 + Math.cos(angle) * BLOB_DIST,
            cy: 50 + Math.sin(angle) * BLOB_DIST,
            r: BLOB_RADIUS
        };
    }) as [AgentAvatarBlob, AgentAvatarBlob, AgentAvatarBlob];

    return { blobs };
}
